import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Button from '../button/CoreButton';
import IconsButton from '../button/IconsButton';
import {
    DialogTitleWrap,
    DisplayDialogTitle,
    StyledDialogActions,
    StyledDialogContent,
    ThemedDialog,
} from '../dialog/CoreDialogStyles';
import { BodySmall } from '../text/Typography';
import RateReviewFields from './RateReviewFields';
import RatingSummary, { IRatingStats } from './RatingSummary';

/** What the user submitted. `comment` is trimmed and may be empty — it is always optional. */
export interface IReviewSubmission {
    rating: number;
    comment: string;
}

export interface IRateReviewDialogProps {
    open: boolean;
    /** Name of the thing being reviewed — a skill, connector, tool, or Cephable feature. */
    subject: string;
    /** Aggregate figures shown above the rating input. Omit to hide the strip entirely. */
    stats?: IRatingStats;
    /** The caller's existing rating, so returning users see and can amend their own vote. */
    initialRating?: number;
    initialComment?: string;
    /** Drop the comment field when a surface only collects a score. */
    hideComment?: boolean;
    /** Disables the actions and shows the submit button as busy while the caller saves. */
    submitting?: boolean;
    /** Fired once a rating is chosen and submit is pressed. The caller closes the dialog. */
    onSubmit: (submission: IReviewSubmission) => void;
    onClose: () => void;
}

/**
 * Collects one person's rating and optional written review for a skill, connector, tool, or Cephable
 * feature. Presentational and controlled — it owns only the in-progress rating and comment, and hands
 * a finished {@link IReviewSubmission} to `onSubmit` so each surface can persist it against its own
 * API (org-scoped feedback, public marketplace ratings, and so on) and then close.
 *
 * Pair it with `RatingSummary` for the resting state, or use `RatingReview`, which wires the two
 * together.
 */
export default function RateReviewDialog({
    open,
    subject,
    stats,
    initialRating,
    initialComment,
    hideComment = false,
    submitting = false,
    onSubmit,
    onClose,
}: Readonly<IRateReviewDialogProps>) {
    const { t } = useTranslation('common');

    const [rating, setRating] = useState(initialRating ?? 0);
    const [comment, setComment] = useState(initialComment ?? '');

    // Reopening is a fresh review: start from whatever the caller now knows the user's vote to be.
    useEffect(() => {
        if (open) {
            setRating(initialRating ?? 0);
            setComment(initialComment ?? '');
        }
    }, [open, initialRating, initialComment]);

    const canSubmit = rating > 0 && !submitting;

    return (
        <ThemedDialog open={open} fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitleWrap>
                <Heading>
                    <DisplayDialogTitle>{t('rating.review.title')}</DisplayDialogTitle>
                    <Subtitle>
                        <Trans>
                            {t('rating.review.subtitle', {
                                subject,
                            })}
                        </Trans>
                    </Subtitle>
                </Heading>
                <IconsButton label={t('close')} startIcon="closeX" $color="simple" hideBorder onClick={onClose} />
            </DialogTitleWrap>
            <StyledDialogContent>
                {stats && (
                    <StatsStrip>
                        <RatingSummary {...stats} />
                    </StatsStrip>
                )}

                <RateReviewFields
                    rating={rating}
                    onRatingChange={setRating}
                    comment={comment}
                    onCommentChange={setComment}
                    hideComment={hideComment}
                    disabled={submitting}
                />
            </StyledDialogContent>
            <StyledDialogActions>
                <Button $color="text" disabled={submitting} onClick={onClose}>
                    {t('cancel')}
                </Button>
                <Button
                    $color="primary"
                    disabled={!canSubmit}
                    onClick={() => onSubmit({ rating, comment: comment.trim() })}>
                    {t('rating.review.submit')}
                </Button>
            </StyledDialogActions>
        </ThemedDialog>
    );
}

const Heading = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled(BodySmall)`
    color: ${(props) => props.theme.color.textBody};

    /* The translation names the subject inside <strong> — see the Trans usage above. */
    strong {
        font-weight: 700;
        color: ${(props) => props.theme.color.text};
    }
`;

/** Hairlines above and below the aggregate figures, separating them from the heading and the input. */
const StatsStrip = styled.div`
    padding: 0.75rem 0;
    margin: 0.75rem 0;
    border-bottom: 1px solid ${(props) => props.theme.color.border};
`;
